// hooks/useReviewDeadlinePicker.ts
import { useState, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import dayjs from 'dayjs'

const useReviewDeadlinePicker = () => {
  const { watch, setValue, triggerValidation } = useFormContext()

  const reviewToTs = watch('review_to_ts') ? new Date(watch('review_to_ts')) : null
  const deadline = watch('deadline')
  const allowedOverdueDays = watch('allowed_overdue_days')
  const isRecurring = watch('is_recurring')

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  // The server computes to_ts as deadline + allowed_overdue_days. review_to_ts must be >= that.
  const effectiveToTs = useMemo(
    () => dayjs(deadline).add(Number(allowedOverdueDays || 0), 'day'),
    [deadline, allowedOverdueDays]
  )

  const reviewTime = useMemo(() => (reviewToTs ? dayjs(reviewToTs).format('HH:mm') : ''), [reviewToTs])

  const handleDateChange = async (date: Date | null) => {
    if (date) {
      const current = watch('review_to_ts')
      const updatedReviewToTs = dayjs(date)
        .set('hour', dayjs(current || effectiveToTs).hour())
        .set('minute', dayjs(current || effectiveToTs).minute())
        .toISOString()

      setValue('review_to_ts', updatedReviewToTs)
      await triggerValidation('review_to_ts')
    }
  }

  const handleTimeChange = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number)
    const current = watch('review_to_ts')
    const updatedReviewToTs = dayjs(current || effectiveToTs)
      .set('hour', hours)
      .set('minute', minutes)
      .toISOString()

    setValue('review_to_ts', updatedReviewToTs)
    triggerValidation('review_to_ts')
  }

  const clearReviewDate = () => {
    setValue('review_to_ts', null)
    triggerValidation('review_to_ts')
  }

  const openDatePicker = () => setIsDatePickerOpen(true)

  const closeDatePicker = () => setIsDatePickerOpen(false)

  return {
    reviewToTs,
    isRecurring,
    effectiveToTs,
    reviewTime,
    isDatePickerOpen,
    handleDateChange,
    handleTimeChange,
    clearReviewDate,
    openDatePicker,
    closeDatePicker,
  }
}

export default useReviewDeadlinePicker
